import { useCallback, useState } from 'react';
import { useQuery, useQueryClient, type UseQueryResult } from 'react-query';
import { apiClient } from '../services/api';
import type {
  DashboardAgentNetworkData,
  DashboardExecutiveData,
  DashboardOverviewData,
  DashboardPeriodParams,
  DashboardPlatformPlayersData,
  DashboardShopPlayersData,
  DashboardV2Envelope,
} from '../types';

function tabQueryOptions(period: DashboardPeriodParams['period']) {
  const isToday = period === 'today';
  return {
    staleTime: isToday ? 60 * 1000 : 5 * 60 * 1000,
    // Poll only while the tab is focused; avoid background spam and error storms.
    refetchInterval: isToday ? 60 * 1000 : (false as const),
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: false,
    retry: 1,
    keepPreviousData: true,
  };
}

type DashboardV2QueryResult<T extends DashboardV2Envelope> = UseQueryResult<T, Error> & {
  refresh: () => Promise<void>;
  refreshing: boolean;
};

function isPeriodQueryReady(params: DashboardPeriodParams): boolean {
  if (params.period !== 'custom') return true;
  return Boolean(params.dateFrom && params.dateTo);
}

function useDashboardV2Query<T extends DashboardV2Envelope>(
  queryKey: string,
  fetcher: (params: DashboardPeriodParams, refresh?: boolean) => Promise<T>,
  params: DashboardPeriodParams,
  enabled = true,
): DashboardV2QueryResult<T> {
  const queryClient = useQueryClient();
  const [refreshing, setRefreshing] = useState(false);
  const canFetch = enabled && isPeriodQueryReady(params);

  const query = useQuery<T, Error>(
    [queryKey, params],
    () => fetcher(params),
    { ...tabQueryOptions(params.period), enabled: canFetch },
  );

  const refresh = useCallback(async () => {
    if (!isPeriodQueryReady(params)) return;
    setRefreshing(true);
    try {
      const data = await fetcher(params, true);
      queryClient.setQueryData([queryKey, params], data);
    } finally {
      setRefreshing(false);
    }
  }, [queryKey, params, fetcher, queryClient]);

  return { ...query, refresh, refreshing };
}

/** Overview tab — executive summary (KPI strip + charts). */
export const useDashboardV2Executive = (
  params: DashboardPeriodParams,
  enabled = true,
): DashboardV2QueryResult<DashboardExecutiveData> =>
  useDashboardV2Query<DashboardExecutiveData>(
    'dashboardV2Executive',
    (p, r) => apiClient.getDashboardExecutive(p, r),
    params,
    enabled,
  );

/** Financials tab — the platform ledger view (formerly the Overview tab). */
export const useDashboardV2Overview = (
  params: DashboardPeriodParams,
  enabled = true,
): DashboardV2QueryResult<DashboardOverviewData> =>
  useDashboardV2Query<DashboardOverviewData>(
    'dashboardV2Overview',
    (p, r) => apiClient.getDashboardOverview(p, r),
    params,
    enabled,
  );

export const useDashboardV2PlatformPlayers = (
  params: DashboardPeriodParams,
  enabled = true,
): DashboardV2QueryResult<DashboardPlatformPlayersData> =>
  useDashboardV2Query<DashboardPlatformPlayersData>(
    'dashboardV2PlatformPlayers',
    (p, r) => apiClient.getDashboardPlatformPlayers(p, r),
    params,
    enabled,
  );

export const useDashboardV2ShopPlayers = (
  params: DashboardPeriodParams,
  enabled = true,
): DashboardV2QueryResult<DashboardShopPlayersData> =>
  useDashboardV2Query<DashboardShopPlayersData>(
    'dashboardV2ShopPlayers',
    (p, r) => apiClient.getDashboardShopPlayers(p, r),
    params,
    enabled,
  );

export const useDashboardV2AgentNetwork = (
  params: DashboardPeriodParams,
  enabled = true,
): DashboardV2QueryResult<DashboardAgentNetworkData> =>
  useDashboardV2Query<DashboardAgentNetworkData>(
    'dashboardV2AgentNetwork',
    (p, r) => apiClient.getDashboardAgentNetwork(p, r),
    params,
    enabled,
  );
